import { X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface ResourcePillProps {
  /** Type glyph, shown in the leading disc. */
  icon: LucideIcon;
  /** Fill for the leading disc — carries the per-type colour. */
  iconWrapClass: string;
  /** Colour for the glyph itself. */
  iconClass: string;
  /** Filename, link title or text-snippet name. Truncates. */
  label: string;
  onRemove: () => void;
  /** Accessible name for the remove button, e.g. `Remove notes.pdf`. */
  removeLabel: string;
}

/**
 * One attached resource in the composer's Resources row — a file, a link or a
 * block of pasted text.
 *
 * Hand-rolled rather than a shared primitive: `@ai-tutor/ui` has no generic
 * `Badge`, and its `StatusBadge` is a text-only tinted span keyed to semantic
 * variants (success / warning / info / learning types), so it cannot host the
 * icon disc + truncating label + remove button this needs.
 *
 * The three call sites in `LearningPrompt` were three duplicated blocks of
 * identical markup differing only in the disc's colours and the label source.
 * Colour comes in as props rather than a `type` union because files already
 * resolve theirs through `getFileTypeInfo` (eight MIME branches), and
 * duplicating that mapping here would give the pill two sources of truth.
 *
 * `bg-ground` in light, NOT `bg-muted`. In light `--muted` is
 * `225.8 58.9% 88.9%` (rgb 210,218,243) against a `--card` panel at 97.8%
 * lightness — a 9-point drop in the near-white range, which made a row of
 * pills read as heavy grey-blue slabs. Dark was already right because there
 * `--muted` (18.2%) sits just 6 points above `--card` (12.1%), a gentle lift;
 * `--ground` is the light-mode counterpart of that gentleness at 93.7%.
 *
 * There is deliberately no hover state on the pill. It carried a
 * `hover:bg-muted` that named the same colour as its rest state, so it had
 * never painted anything — and the pill is not the click target, the X is.
 *
 * `inline-flex`, NOT `flex`. A block-level flex container fills its parent, so
 * a standalone pill stretched to the full width of whatever contained it — 1200px
 * in the catalog — leaving the icon, the 140px label and the X clustered at the
 * left of an enormous rounded slab. It looked correct in the product only
 * because `LearningPrompt` puts them in a `flex` row, where `shrink-0` sizes
 * them to content. A flex item's display is blockified, so inside that row
 * `inline-flex` behaves identically; everywhere else it now shrink-wraps.
 */
export function ResourcePill({
  icon: Icon,
  iconWrapClass,
  iconClass,
  label,
  onRemove,
  removeLabel,
}: ResourcePillProps) {
  return (
    <div className='inline-flex shrink-0 items-center gap-2 rounded-full border border-border bg-ground py-1.5 pl-1.5 pr-2 transition-colors dark:bg-muted'>
      <div
        className={`flex h-7 w-7 items-center justify-center rounded-full ${iconWrapClass}`}
      >
        <Icon className={`h-3.5 w-3.5 ${iconClass}`} />
      </div>
      <span className='max-w-[140px] truncate text-xs font-medium text-foreground'>
        {label}
      </span>
      {/* `aria-label` rather than relying on the bare X: three pills in a row
          all named "remove" is unusable by voice or screen reader, so the
          caller passes the resource name in. */}
      <button
        type='button'
        aria-label={removeLabel}
        // 32px on a phone (20px from `sm`), with a 44px `tap-target`: the
        // pill grows to 44 tall so the tap area fits INSIDE it - the row
        // scrolls sideways and would clip anything taller - and its 6px of
        // sideways reach stays within the pill's padding and the 8px gap, so
        // a tap on the next pill can never remove this one.
        className='tap-target flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive dark:hover:bg-destructive/20 sm:h-5 sm:w-5'
        onClick={onRemove}
      >
        <X className='h-3 w-3' />
      </button>
    </div>
  );
}
